import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { SERVICES } from "../lib/content";
import { PaymentButtons } from "./PaymentButtons";

const ServiceCard = ({ svc, index, onConsult }) => (
  <motion.div
    id={svc.id}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.75, delay: (index % 2) * 0.1, ease: [0.16, 1, 0.3, 1] }}
    className="group relative scroll-mt-28 bg-zinc-950/60 border border-zinc-800/80 hover:border-amber-500/50 transition-colors duration-500 p-8 sm:p-10 flex flex-col"
  >
    <div className="absolute top-0 left-0 h-px w-0 bg-gold-foil group-hover:w-full transition-all duration-700" />
    <div className="flex items-start justify-between">
      <span className="font-serif text-5xl text-amber-500/25 group-hover:text-amber-500/60 transition-colors duration-500">
        {svc.index}
      </span>
      {svc.price && !svc.purchase && (
        <span className="flex flex-col items-end">
          <span className="font-serif text-3xl font-black text-gold-foil leading-none">
            {svc.price}
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
            Starting Investment
          </span>
        </span>
      )}
    </div>
    <h3 className="mt-6 font-serif text-2xl sm:text-3xl font-semibold text-white">
      {svc.title}
    </h3>
    <p className="mt-2 text-amber-400 font-medium text-sm tracking-wide">
      {svc.headline}
    </p>
    <p className="mt-5 text-zinc-400 font-light leading-relaxed text-[15px]">
      {svc.intro}
    </p>

    <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
      {svc.items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm text-zinc-300">
          <Check className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>

    {svc.note && (
      <div className="mt-7 bg-amber-500/10 border border-amber-500/30 px-5 py-4">
        <p className="text-amber-300 text-sm font-semibold leading-snug">{svc.note}</p>
      </div>
    )}

    {svc.purchase ? (
      <div className="mt-8">
        <PaymentButtons
          total={svc.purchase.total}
          fullKey={svc.purchase.fullKey}
          installmentKey={svc.purchase.installmentKey}
          installmentAmount={svc.purchase.installmentAmount}
          installment3Key={svc.purchase.installment3Key}
          installment3Amount={svc.purchase.installment3Amount}
          testid={`service-${svc.id}`}
        />
        <button
          onClick={onConsult}
          data-testid={`service-cta-${svc.id}`}
          className="mt-4 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-amber-400 hover:text-amber-200 transition-colors"
        >
          Or Book a Free 15-Minute Consultation
          <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    ) : (
      <button
        onClick={onConsult}
        data-testid={`service-cta-${svc.id}`}
        className="mt-8 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-amber-400 hover:text-amber-200 transition-colors self-start"
      >
        Get Started
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
    )}
  </motion.div>
);

export const Services = ({ onConsult }) => (
  <section id="services" className="relative bg-[#221b14] py-24 sm:py-36 scroll-mt-20">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="max-w-3xl">
        <Reveal>
          <Overline>Our Services</Overline>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-serif font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            Complete Financial &amp; Business{" "}
            <span className="text-gold-foil">Solutions</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-zinc-400 font-light text-lg">
            Five core practice areas designed to recover, protect, and grow your
            wealth at every stage.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
        {SERVICES.map((svc, i) => (
          <ServiceCard key={svc.id} svc={svc} index={i} onConsult={onConsult} />
        ))}
      </div>
    </div>
  </section>
);
